import { useNavigate } from "react-router-dom";

// ============================================
// PRIVACY POLICY PAGE
// Publicly accessible (outside SignedIn wrapper)
// Placeholder until full privacy policy is drafted
// ============================================

function IconShield({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
    </svg>
  );
}

function IconArrowLeft({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
    </svg>
  );
}

export default function Privacy() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors"
          >
            <IconArrowLeft className="w-5 h-5 text-slate-500" />
          </button>
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center shadow-sm">
              <IconShield className="w-4 h-4 text-white" />
            </div>
            <span className="font-semibold text-slate-800">PeerPrep</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-6 py-10">
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8 sm:p-12">
          <h1 className="text-2xl font-bold text-slate-900 mb-1">Privacy Policy</h1>
          <p className="text-sm text-slate-500 mb-8">Last Updated: March 6, 2026</p>

          <div className="prose prose-slate prose-sm max-w-none
            prose-headings:text-slate-800 prose-headings:font-semibold
            prose-h2:text-lg prose-h2:mt-10 prose-h2:mb-4 prose-h2:pt-6 prose-h2:border-t prose-h2:border-slate-100
            prose-h3:text-base prose-h3:mt-6 prose-h3:mb-3
            prose-p:text-slate-600 prose-p:leading-relaxed
            prose-a:text-teal-600 prose-a:no-underline hover:prose-a:underline
            prose-strong:text-slate-700
          ">
            <p>
              PeerPrep ("we," "us," or "our") is operated by Jamie Harkey. This Privacy Policy describes how we collect, use, and protect your information when you use our Service.
            </p>

            <h2>De-Identified by Design</h2>
            <p>
              PeerPrep is architected to avoid collecting Protected Health Information (PHI). Clinical text you enter is processed through a client-side de-identification pipeline before reaching our servers. We do not require, request, or knowingly store patient names, dates of birth, Social Security numbers, medical record numbers, or other direct patient identifiers.
            </p>

            <h2>Information We Collect</h2>
            <p>We collect the following categories of information:</p>
            <p><strong>Account Information:</strong> Your name, email address, professional credentials, and practice information provided during registration.</p>
            <p><strong>Usage Data:</strong> Features accessed, cases created, session duration, and interactions with the Service.</p>
            <p><strong>Technical Data:</strong> Device type, browser, operating system, IP address, and general location (city/region level).</p>
            <p><strong>De-Identified Clinical Data:</strong> Clinical text that has been processed through our de-identification pipeline. This data does not contain patient identifiers.</p>

            <h2>How We Use Your Information</h2>
            <p>We use your information to:</p>
            <p>(a) Provide, operate, and improve the Service;</p>
            <p>(b) Authenticate your identity and manage your account;</p>
            <p>(c) Communicate with you about the Service, including updates and support;</p>
            <p>(d) Analyze usage patterns to improve clinical decision support quality;</p>
            <p>(e) Ensure compliance with our Terms of Service.</p>

            <h2>What We Do Not Do</h2>
            <p>We do not sell, license, or share your personal information or usage data with insurance companies, data brokers, pharmaceutical companies, or any third party for marketing or commercial purposes unrelated to operating the Service.</p>
            <p>We do not use your clinical data to train machine learning models that are sold or licensed to third parties.</p>
            <p>We do not display advertisements in the Service.</p>

            <h2>Third-Party Services</h2>
            <p>We use the following third-party services to operate PeerPrep:</p>
            <p><strong>Clerk:</strong> Authentication and account management.</p>
            <p><strong>Supabase:</strong> Database and backend infrastructure.</p>
            <p><strong>Anthropic:</strong> AI-powered clinical decision support (receives only de-identified text).</p>
            <p><strong>Vercel:</strong> Application hosting and deployment.</p>
            <p>Each of these providers has their own privacy policies. We select providers that maintain appropriate security practices.</p>

            <h2>Data Security</h2>
            <p>
              We implement reasonable technical and organizational measures to protect your information, including encryption in transit (HTTPS/TLS), access controls, and client-side de-identification of clinical data before transmission.
            </p>

            <h2>Data Retention</h2>
            <p>
              We retain your account information for as long as your account is active. If you delete your account, we will delete your personal information within 30 days, except as required by law or legitimate business purposes (such as resolving disputes).
            </p>

            <h2>Your Rights</h2>
            <p>You may:</p>
            <p>(a) Access the personal information we hold about you;</p>
            <p>(b) Request correction of inaccurate information;</p>
            <p>(c) Request deletion of your account and associated data;</p>
            <p>(d) Export your case data.</p>
            <p>To exercise these rights, contact us at <a href="mailto:jamieharkeyconsulting@gmail.com">jamieharkeyconsulting@gmail.com</a>.</p>

            <h2>Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. Material changes will be communicated via email or in-app notification at least thirty (30) days before they take effect.
            </p>

            <h2>Contact</h2>
            <p>
              If you have questions about this Privacy Policy, please contact us at:<br />
              <strong>Jamie Harkey</strong><br />
              Email: <a href="mailto:jamieharkeyconsulting@gmail.com">jamieharkeyconsulting@gmail.com</a>
            </p>
          </div>
        </div>
      </div>

      <div className="h-16" />
    </div>
  );
}